"use client";

import {
  cn,
  Icon,
  ModalContent,
  ModalFooter,
  ModalHeader,
  PortalModal,
  TIconKey,
} from "@cloai/uikit";
import { motion } from "framer-motion";
import { isEmpty, map } from "lodash";
import { useLocale, useTranslations } from "next-intl";
import { memo, useMemo } from "react";

import { AiRequest } from "@/.gql/graphql";
import {
  PhotoSettings,
  useUndressContext,
  useUndressSettingsContext,
} from "@/providers";
import { getLocalizedName } from "@/utils/getLocalizedName";

import { ItemCard } from "../components/ItemCard";
import { STYLES } from "../styles";
import { MOTION_VARIANTS } from "../vars";
import Tabs from "./Tabs";
import { useClickSettings } from "./useClickSettings";
import { PORTALS } from ".constants/portals";

export const CollectionsPortal = memo(() => {
  const t = useTranslations("UserFlowModal");
  const s = useTranslations("HomePage.settingsPopup");
  const { clickSettings, selectedCollectionCount } = useUndressContext();
  const { wrapperRef, sectionsRef, handleScroll, sets, storylines } =
    useUndressSettingsContext();

  const { set, storyline } = clickSettings;

  const locale = useLocale();
  const handleItemClick = useClickSettings();

  /**
   * Функция для установки ссылки (ref) на секцию с настройками.
   *
   * @param {number} index - Индекс секции.
   * @returns {function} Функция, которая устанавливает ref на элемент секции.
   */
  const setSectionRef = (index: number) => (el: HTMLDivElement | null) => {
    if (el) {
      sectionsRef.current.set(index, el);
    }
  };

  /**
   * Рендерит секцию настроек (одежда, позы, сеты).
   *
   * @param {number} index - Индекс секции.
   * @param {AiRequest[]} items - Данные для рендера.
   * @param {string | number} selectedItem - Текущее выбранное значение.
   * @param {keyof PhotoSettings} itemType - Тип настройки (`costume`, `pose`, `set`).
   * @param {AiRequest[]} [extraItems=[]] - Дополнительные элементы (например, `noSelectedItem`).
   * @returns {JSX.Element | null} Секция настроек или null, если данные пустые.
   */
  const renderSection = (
    indexSection: number,
    items: AiRequest[],
    selectedItem: string | number,
    itemType: keyof PhotoSettings,
    titleText?: string,
    titleIcon?: TIconKey,
  ) =>
    !isEmpty(items) && (
      <motion.div
        key={indexSection}
        ref={setSectionRef(indexSection)}
        variants={MOTION_VARIANTS.container}
        initial="hidden"
        animate="visible"
        className="flex flex-col gap-popup-gap"
      >
        {titleText && (
          <motion.span
            key={`subtitle-${itemType}`}
            variants={MOTION_VARIANTS.item}
            className="flex items-center justify-center gap-1 text-center text-balance opacity-80"
          >
            {titleIcon && <Icon icon={titleIcon} />}
            {titleText}
          </motion.span>
        )}

        <div
          role="grid"
          aria-label="Collections grid"
          className={cn(STYLES.grid.variants.big, STYLES.grid.animate)}
        >
          {map(items, (item, Index) => (
            <motion.div
              key={item.id}
              variants={MOTION_VARIANTS.item}
              custom={Index}
              role="gridcell"
              aria-label={getLocalizedName(item, locale)}
            >
              <ItemCard
                item={item}
                index={Index}
                onClick={handleItemClick(itemType, item.id)}
                variant="wide"
              />
            </motion.div>
          ))}
        </div>
      </motion.div>
    );

  /**
   * Тип для элемента списка настроек.
   * Описывает структуру данных для рендера секции настроек (одежда, позы, сеты).
   *
   * @typedef {Object} ListItem
   * @property {AiRequest[]} items - Массив элементов для отображения (костюмы, позы или сеты)
   * @property {string | number} selectedItem - Идентификатор выбранного элемента
   * @property {keyof PhotoSettings} itemType - Тип секции ('costume' | 'pose' | 'set')
   * @property {string} [titleText] - Опциональный заголовок секции
   * @property {string} [titleIcon] - Опциональная иконка секции
   */
  type SettingsListProps = {
    items: AiRequest[];
    selectedItem: string | number;
    itemType: keyof PhotoSettings;
    titleText?: string;
    titleIcon?: TIconKey;
  }[];

  /**
   * Мемоизированный список секций настроек.
   * Формирует массив объектов для рендера секций в зависимости от AB-теста.
   *
   * @type {List}
   */
  const settingsList: SettingsListProps = useMemo(
    () =>
      [
        !isEmpty(storylines) && {
          items: storylines,
          selectedItem: storyline,
          itemType: "storyline",
          extraItems: [],
          titleText: s("settingsStorylinesNote"),
          titleIcon: "cards_star",
        },
        !isEmpty(sets) && {
          items: sets,
          selectedItem: set,
          itemType: "set",
          extraItems: [],
          titleText: s("settingsSetsNote", {
            count: selectedCollectionCount || 9,
          }),
          titleIcon: "filter",
        },
      ].filter(Boolean) as SettingsListProps,
    [storylines, storyline, s, sets, set, selectedCollectionCount],
  );

  return (
    <PortalModal
      portalId={PORTALS.USER_FLOW_COLLECTIONS}
      style={STYLES.portal.variants.wide}
    >
      <ModalHeader>{t("titles.chooseCollection")}</ModalHeader>

      <ModalContent
        className="@container"
        ref={wrapperRef}
        onScroll={handleScroll}
      >
        {map(
          settingsList,
          ({ items, selectedItem, itemType, titleText, titleIcon }, Index) =>
            renderSection(
              Index,
              items,
              selectedItem,
              itemType,
              titleText,
              titleIcon,
            ),
        )}
      </ModalContent>

      <ModalFooter>
        <Tabs />
      </ModalFooter>
    </PortalModal>
  );
});

CollectionsPortal.displayName = "CollectionsPortal";
